import React, { useCallback, useEffect, useState, useRef } from 'react';
import axios from '../../axiosConfig';
import '../../styles/manageDeliverables/books.css';
import { useNavigate } from 'react-router-dom';
import ReactDOM from 'react-dom';

const GRADE_TONES = ['primary', 'mint', 'peach', 'lilac', 'sky', 'rose'];

function pageItems(current, total) {
  if (total <= 7) {
    return Array.from({ length: total }, (_, i) => i + 1);
  }
  const items = [1];
  if (current > 3) items.push('…');
  for (let p = Math.max(2, current - 1); p <= Math.min(total - 1, current + 1); p += 1) {
    if (!items.includes(p)) items.push(p);
  }
  if (current < total - 2) items.push('…');
  if (!items.includes(total)) items.push(total);
  return items;
}

const groupKey = (group) =>
  [group.title, group.isbn_code, group.grade_id, group.version_label].map((v) => v ?? '').join('|');

const getGroupBookIds = (group) => {
  if (Array.isArray(group.book_ids) && group.book_ids.length) {
    return [...new Set(group.book_ids.map(Number).filter(Boolean))];
  }
  const ids = [];
  if (group.digital?.book_id) ids.push(Number(group.digital.book_id));
  if (group.print?.book_id) ids.push(Number(group.print.book_id));
  return [...new Set(ids)];
};

const BooksTable = () => {
  const [books, setBooks] = useState([]);
  const [grades, setGrades] = useState([]);
  const [subjects, setSubjects] = useState([]);
  const [languages, setLanguages] = useState([]);
  const [standards, setStandards] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterGrade, setFilterGrade] = useState('');
  const [filterSubject, setFilterSubject] = useState('');
  const [filterCountry, setFilterCountry] = useState('');
  const [filterLanguage, setFilterLanguage] = useState('');
  const [filterStandard, setFilterStandard] = useState('');
  const [filterFormat, setFilterFormat] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10);
  const [error, setError] = useState(null);
  const [deleteMenuAnchor, setDeleteMenuAnchor] = useState(null);
  const [deleteMenuGroup, setDeleteMenuGroup] = useState(null);
  const [selectedKeys, setSelectedKeys] = useState([]);
  const [assignModalOpen, setAssignModalOpen] = useState(false);
  const [assignUserIds, setAssignUserIds] = useState([]);
  const [assignUserSearch, setAssignUserSearch] = useState('');
  const [assigning, setAssigning] = useState(false);
  const [assigneesModal, setAssigneesModal] = useState(null);
  const [unassigningUserId, setUnassigningUserId] = useState(null);
  const navigate = useNavigate();

  const authHeaders = () => ({
    Authorization: `Bearer ${localStorage.getItem('token') || ''}`,
  });

  const getGradeName = (id) => grades.find((g) => g.grade_id === id)?.grade_level || '—';
  const getSubjectName = (id) => subjects.find((s) => s.subject_id === id)?.subject_name || '—';
  const getLanguageName = (id) => languages.find((l) => l.language_id === id)?.language_name || '—';
  const getStandardName = (id) => standards.find((st) => st.standard_id === id)?.standard_name || '—';
  const formatShortDate = (value) => {
    if (!value) return '—';
    return new Date(value).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  const countryOptions = [...new Map(
    books
      .filter((g) => g.country_id != null || g.country_name)
      .map((g) => [String(g.country_id ?? g.country_name), {
        id: String(g.country_id ?? g.country_name),
        label: g.country_name || `Country ${g.country_id}`,
      }])
  ).values()].sort((a, b) => a.label.localeCompare(b.label));

  const hasActiveFilters =
    Boolean(searchTerm.trim()) ||
    Boolean(filterGrade) ||
    Boolean(filterSubject) ||
    Boolean(filterCountry) ||
    Boolean(filterLanguage) ||
    Boolean(filterStandard) ||
    Boolean(filterFormat);

  const clearFilters = () => {
    setSearchTerm('');
    setFilterGrade('');
    setFilterSubject('');
    setFilterCountry('');
    setFilterLanguage('');
    setFilterStandard('');
    setFilterFormat('');
    setCurrentPage(1);
  };

  const filteredBooks = books.filter((group) => {
    const lowerCaseSearchTerm = searchTerm.toLowerCase().trim();
    const matchesSearch =
      !lowerCaseSearchTerm ||
      (group.title || '').toLowerCase().includes(lowerCaseSearchTerm) ||
      (group.country_name || '').toLowerCase().includes(lowerCaseSearchTerm) ||
      (group.isbn_code || '').toLowerCase().includes(lowerCaseSearchTerm) ||
      (group.book_type_title || '').toLowerCase().includes(lowerCaseSearchTerm) ||
      getLanguageName(group.language_id).toLowerCase().includes(lowerCaseSearchTerm) ||
      getGradeName(group.grade_id).toLowerCase().includes(lowerCaseSearchTerm) ||
      getSubjectName(group.subject_id).toLowerCase().includes(lowerCaseSearchTerm) ||
      getStandardName(group.standard_id).toLowerCase().includes(lowerCaseSearchTerm) ||
      group.tags?.some((tag) => tag.tag_name.toLowerCase().includes(lowerCaseSearchTerm));

    const matchesGrade = !filterGrade || String(group.grade_id) === filterGrade;
    const matchesSubject = !filterSubject || String(group.subject_id) === filterSubject;
    const matchesCountry =
      !filterCountry ||
      String((group.country_id ?? group.country_name) || '') === filterCountry;
    const matchesLanguage = !filterLanguage || String(group.language_id) === filterLanguage;
    const matchesStandard = !filterStandard || String(group.standard_id) === filterStandard;
    const matchesFormat =
      !filterFormat ||
      (filterFormat === 'digital' && Boolean(group.digital)) ||
      (filterFormat === 'print' && Boolean(group.print));

    return (
      matchesSearch &&
      matchesGrade &&
      matchesSubject &&
      matchesCountry &&
      matchesLanguage &&
      matchesStandard &&
      matchesFormat
    );
  });

  const totalPages = Math.ceil(filteredBooks.length / itemsPerPage) || 1;
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = filteredBooks.slice(indexOfFirstItem, indexOfLastItem);
  const pageKeys = currentItems.map(groupKey);
  const allPageSelected = pageKeys.length > 0 && pageKeys.every((k) => selectedKeys.includes(k));
  const somePageSelected = pageKeys.some((k) => selectedKeys.includes(k));

  const fetchData = useCallback(async () => {
    try {
      const headers = authHeaders();
      const [booksRes, gradesRes, subjectsRes, languagesRes, standardRes, usersRes] = await Promise.all([
        axios.get('/books/grouped', { headers }),
        axios.get('/grades', { headers }),
        axios.get('/subjects', { headers }),
        axios.get('/languages', { headers }),
        axios.get('/standards', { headers }),
        axios.get('/user', { headers }).catch(() => ({ data: { users: [] } })),
      ]);

      setBooks(booksRes.data.books || []);
      setGrades(gradesRes.data || []);
      setSubjects(subjectsRes.data || []);
      setLanguages(languagesRes.data || []);
      setStandards(standardRes.data || []);
      setUsers((usersRes.data.users || []).filter((u) => u.role !== 'admin'));
      setLoading(false);
      setError(null);
    } catch (err) {
      console.error('Error fetching book data:', err);
      setError(err.response?.data?.error || 'Failed to fetch books');
      setLoading(false);
    }
  }, []);

  const handleDelete = async (bookId, e) => {
    e.stopPropagation();
    if (!window.confirm('Are you sure you want to delete this book? This action cannot be undone.')) {
      return;
    }

    try {
      await axios.delete(`/books/${bookId}`, { headers: authHeaders() });
      fetchData();
    } catch (err) {
      console.error('Error deleting book:', err);
      setError(err.response?.data?.error || 'Failed to delete book');
    }
  };

  const handleCSVUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const fileName = file.name.toLowerCase();
    if (!fileName.endsWith('.csv') && !fileName.endsWith('.xls') && !fileName.endsWith('.xlsx')) {
      alert('Please upload a CSV, XLS, or XLSX file');
      return;
    }

    if (!window.confirm(`Upload ${file.name}? This will create book entries from the CSV data.`)) {
      e.target.value = '';
      return;
    }

    try {
      setLoading(true);
      const formData = new FormData();
      formData.append('csv_file', file);

      const response = await axios.post('/books/upload-csv', formData, {
        headers: {
          ...authHeaders(),
          'Content-Type': 'multipart/form-data',
        },
      });

      const { success, errors, details } = response.data;
      let message = `Upload completed!\n`;
      message += `✅ Successfully created: ${success} books\n`;
      if (errors > 0) {
        message += `❌ Errors: ${errors} rows\n\n`;
        message += `Error details:\n`;
        details.errors.slice(0, 10).forEach((err) => {
          message += `Row ${err.row}: ${err.error}\n`;
        });
        if (details.errors.length > 10) {
          message += `... and ${details.errors.length - 10} more errors`;
        }
      }

      alert(message);
      await fetchData();
    } catch (err) {
      console.error('Error uploading CSV:', err);
      alert(err.response?.data?.error || 'Failed to upload CSV file');
    } finally {
      setLoading(false);
      e.target.value = '';
    }
  };

  const toggleRow = (key) => {
    setSelectedKeys((prev) => (prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key]));
  };

  const togglePage = () => {
    if (allPageSelected) {
      setSelectedKeys((prev) => prev.filter((k) => !pageKeys.includes(k)));
      return;
    }
    setSelectedKeys((prev) => [...new Set([...prev, ...pageKeys])]);
  };

  const openAssignModal = () => {
    if (!selectedKeys.length) return;
    setAssignUserIds([]);
    setAssignUserSearch('');
    setAssignModalOpen(true);
  };

  const handleBulkAssign = async () => {
    if (!selectedKeys.length || !assignUserIds.length) return;

    const selectedGroups = books.filter((g) => selectedKeys.includes(groupKey(g)));
    const bookIds = [...new Set(selectedGroups.flatMap(getGroupBookIds))];
    if (!bookIds.length) {
      alert('No book formats found for the selected rows.');
      return;
    }

    setAssigning(true);
    try {
      const headers = { headers: authHeaders() };
      const jobs = [];
      assignUserIds.forEach((userId) => {
        bookIds.forEach((bookId) => {
          jobs.push(
            axios.post(
              '/books/book-control',
              {
                user_id: Number(userId),
                book_id: Number(bookId),
                permission: 'viewer',
                expiry: null,
                can_download: false,
              },
              headers
            )
          );
        });
      });
      await Promise.all(jobs);
      setAssignModalOpen(false);
      setSelectedKeys([]);
      setAssignUserIds([]);
      await fetchData();
      alert(
        `Assigned ${bookIds.length} book format(s) to ${assignUserIds.length} user(s).`
      );
    } catch (err) {
      console.error('Bulk assign failed:', err);
      alert(err.response?.data?.error || 'Failed to assign books to users.');
    } finally {
      setAssigning(false);
    }
  };

  const handleUnassignUserFromGroup = async (group, user) => {
    const bookIds = getGroupBookIds(group);
    if (!bookIds.length || !user?.user_id) return;
    if (
      !window.confirm(
        `Remove "${user.name || user.email}" from "${group.title || 'this book'}"?`
      )
    ) {
      return;
    }

    setUnassigningUserId(user.user_id);
    try {
      const headers = { headers: authHeaders() };
      await Promise.all(
        bookIds.map((bookId) =>
          axios.post(
            '/books/book-control/unshare',
            { user_id: Number(user.user_id), book_id: Number(bookId) },
            headers
          )
        )
      );
      await fetchData();
      setAssigneesModal((prev) => {
        if (!prev) return null;
        const nextUsers = (prev.assigned_users || []).filter((u) => u.user_id !== user.user_id);
        return {
          ...prev,
          assigned_users: nextUsers,
          assigned_users_count: nextUsers.length,
        };
      });
    } catch (err) {
      console.error('Unassign failed:', err);
      alert(err.response?.data?.error || 'Failed to unassign user.');
    } finally {
      setUnassigningUserId(null);
    }
  };

  function Popover({ anchorEl, onClose, children }) {
    const popoverRef = useRef();
    const [style, setStyle] = useState({});

    useEffect(() => {
      if (anchorEl) {
        const rect = anchorEl.getBoundingClientRect();
        let top = rect.top + rect.height / 2;
        let left = rect.right + 8;
        let transform = 'translateY(-50%)';
        if (window.innerWidth - rect.right < 160) {
          left = rect.left - 160;
        }
        if (window.innerHeight - rect.bottom < 80) {
          top = rect.bottom - 80;
          transform = '';
        }
        setStyle({
          position: 'fixed',
          top,
          left,
          transform,
          background: '#fff',
          border: '1px solid #e0e0e0',
          borderRadius: '8px',
          boxShadow: '0 4px 16px rgba(0,0,0,0.10)',
          zIndex: 9999,
          padding: '0.5em 0',
          minWidth: '150px',
          display: 'flex',
          flexDirection: 'column',
          gap: '0.25em',
        });
      }
    }, [anchorEl]);

    useEffect(() => {
      function handleClickOutside(event) {
        if (
          popoverRef.current &&
          !popoverRef.current.contains(event.target) &&
          anchorEl &&
          !anchorEl.contains(event.target)
        ) {
          onClose();
        }
      }
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }, [onClose, anchorEl]);

    if (!anchorEl) return null;
    return ReactDOM.createPortal(
      <div ref={popoverRef} style={style} className="bl-delete-popover">
        {children}
      </div>,
      document.body
    );
  }

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  useEffect(() => {
    if (currentPage > totalPages) setCurrentPage(totalPages);
  }, [currentPage, totalPages]);

  useEffect(() => {
    const valid = new Set(books.map(groupKey));
    setSelectedKeys((prev) => prev.filter((k) => valid.has(k)));
  }, [books]);

  if (loading) return <div className="books-table-loading">Loading books...</div>;
  if (error) return <div className="books-table-error">{error}</div>;

  const showingFrom = filteredBooks.length === 0 ? 0 : indexOfFirstItem + 1;
  const showingTo = Math.min(indexOfLastItem, filteredBooks.length);
  const filteredUsers = users.filter((u) => {
    const q = assignUserSearch.toLowerCase().trim();
    if (!q) return true;
    return (
      String(u.name || '')
        .toLowerCase()
        .includes(q) ||
      String(u.email || '')
        .toLowerCase()
        .includes(q)
    );
  });

  return (
    <div className="books-table-wrapper bl-page">
      <div className="page-head">
        <div>
          <h1 className="page-title">Books List</h1>
          <p className="page-desc">Every deliverable published to the shared library.</p>
        </div>
        <div className="btn-row">
          <label htmlFor="csv-upload" className="btn btn-secondary">
            Upload CSV/Excel
          </label>
          <input
            id="csv-upload"
            type="file"
            accept=".csv,.xls,.xlsx"
            className="bl-file-input"
            onChange={handleCSVUpload}
          />
          <button type="button" className="btn btn-primary" onClick={() => navigate('/admin/add-book')}>
            + Add Book
          </button>
        </div>
      </div>

      <div className="panel">
        <div className="panel-head">
          <span className="panel-title">Books list</span>
          <div className="btn-row bl-toolbar-actions">
            <button
              type="button"
              className="btn btn-primary"
              disabled={!selectedKeys.length}
              onClick={openAssignModal}
            >
              Assign to User{selectedKeys.length ? ` (${selectedKeys.length})` : ''}
            </button>
          </div>
        </div>

        <div className="bl-filter-bar">
          <input
            type="search"
            className="text-input bl-search-input"
            placeholder="Search by title, country, subject…"
            value={searchTerm}
            onChange={(e) => {
              setSearchTerm(e.target.value);
              setCurrentPage(1);
            }}
          />
          <select
            className="text-input bl-filter-select"
            value={filterGrade}
            onChange={(e) => {
              setFilterGrade(e.target.value);
              setCurrentPage(1);
            }}
            aria-label="Filter by grade"
          >
            <option value="">All grades</option>
            {grades.map((g) => (
              <option key={g.grade_id} value={String(g.grade_id)}>
                {g.grade_level}
              </option>
            ))}
          </select>
          <select
            className="text-input bl-filter-select"
            value={filterSubject}
            onChange={(e) => {
              setFilterSubject(e.target.value);
              setCurrentPage(1);
            }}
            aria-label="Filter by subject"
          >
            <option value="">All subjects</option>
            {subjects.map((s) => (
              <option key={s.subject_id} value={String(s.subject_id)}>
                {s.subject_name}
              </option>
            ))}
          </select>
          <select
            className="text-input bl-filter-select"
            value={filterCountry}
            onChange={(e) => {
              setFilterCountry(e.target.value);
              setCurrentPage(1);
            }}
            aria-label="Filter by country"
          >
            <option value="">All countries</option>
            {countryOptions.map((c) => (
              <option key={c.id} value={c.id}>
                {c.label}
              </option>
            ))}
          </select>
          <select
            className="text-input bl-filter-select"
            value={filterLanguage}
            onChange={(e) => {
              setFilterLanguage(e.target.value);
              setCurrentPage(1);
            }}
            aria-label="Filter by language"
          >
            <option value="">All languages</option>
            {languages.map((l) => (
              <option key={l.language_id} value={String(l.language_id)}>
                {l.language_name}
              </option>
            ))}
          </select>
          <select
            className="text-input bl-filter-select"
            value={filterStandard}
            onChange={(e) => {
              setFilterStandard(e.target.value);
              setCurrentPage(1);
            }}
            aria-label="Filter by standard"
          >
            <option value="">All standards</option>
            {standards.map((st) => (
              <option key={st.standard_id} value={String(st.standard_id)}>
                {st.standard_name}
              </option>
            ))}
          </select>
          <select
            className="text-input bl-filter-select"
            value={filterFormat}
            onChange={(e) => {
              setFilterFormat(e.target.value);
              setCurrentPage(1);
            }}
            aria-label="Filter by format"
          >
            <option value="">All formats</option>
            <option value="digital">Digital</option>
            <option value="print">Print</option>
          </select>
          {hasActiveFilters ? (
            <button type="button" className="btn btn-secondary bl-clear-filters" onClick={clearFilters}>
              Clear filters
            </button>
          ) : null}
        </div>

        <div className="table-wrap">
          <table>
            <thead>
              <tr>
                <th className="bl-check-col">
                  <input
                    type="checkbox"
                    className="checkbox-select"
                    checked={allPageSelected}
                    ref={(el) => {
                      if (el) el.indeterminate = !allPageSelected && somePageSelected;
                    }}
                    onChange={togglePage}
                    aria-label="Select all books on this page"
                    disabled={!currentItems.length}
                  />
                </th>
                <th>#</th>
                <th>Book</th>
                <th>Grade</th>
                <th>Country</th>
                <th>Subject</th>
                <th>Format</th>
                <th>Assigned users</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredBooks.length === 0 ? (
                <tr>
                  <td colSpan="9">
                    <div className="empty-note">
                      {hasActiveFilters ? 'No books match the current filters' : 'No books available'}
                    </div>
                  </td>
                </tr>
              ) : (
                currentItems.map((group, index) => {
                  const key = groupKey(group);
                  const language = getLanguageName(group.language_id);
                  const standard = getStandardName(group.standard_id);
                  const gradeTone = GRADE_TONES[index % GRADE_TONES.length];
                  const metaBits = [
                    group.isbn_code ? `ISBN ${group.isbn_code}` : 'No ISBN',
                    group.book_type_title || null,
                    formatShortDate(group.created_at),
                  ].filter(Boolean);
                  const assigneeCount = Array.isArray(group.assigned_users)
                    ? group.assigned_users.length
                    : Number(group.assigned_users_count || 0);
                  const checked = selectedKeys.includes(key);

                  return (
                    <tr key={key} className={checked ? 'bl-row-selected' : undefined}>
                      <td className="bl-check-col">
                        <input
                          type="checkbox"
                          className="checkbox-select"
                          checked={checked}
                          onChange={() => toggleRow(key)}
                          aria-label={`Select ${group.title || 'book'}`}
                        />
                      </td>
                      <td>{indexOfFirstItem + index + 1}</td>
                      <td>
                        <div className="cell-title">{group.title || '—'}</div>
                        <div className="cell-sub">{metaBits.join(' · ')}</div>
                      </td>
                      <td>
                        <span className={`chip chip-${gradeTone}`}>{getGradeName(group.grade_id)}</span>
                      </td>
                      <td>
                        {group.country_name || '—'}
                        <div className="cell-sub">{language}</div>
                      </td>
                      <td>
                        {getSubjectName(group.subject_id)}
                        <div className="cell-sub">{standard}</div>
                      </td>
                      <td>
                        <div className="bl-format-stack">
                          {group.digital ? <span className="chip chip-primary">Digital</span> : null}
                          {group.print ? <span className="chip chip-peach">Print</span> : null}
                          {!group.digital && !group.print ? <span>—</span> : null}
                        </div>
                      </td>
                      <td>
                        <button
                          type="button"
                          className={`bl-assignee-count${assigneeCount ? ' has-users' : ''}`}
                          onClick={() => setAssigneesModal(group)}
                          title={assigneeCount ? 'View assigned users' : 'No users assigned'}
                        >
                          {assigneeCount}
                        </button>
                      </td>
                      <td className="bl-actions-cell">
                        <div className="row-actions bl-row-actions">
                          <button
                            type="button"
                            className="mini-btn"
                            onClick={(e) => {
                              e.stopPropagation();
                              navigate(`/admin/edit-book/${group.digital?.book_id || group.print?.book_id}`);
                            }}
                          >
                            Edit
                          </button>
                          {(group.digital || group.print) && (
                            <button
                              type="button"
                              className="mini-btn danger"
                              onClick={(e) => {
                                e.stopPropagation();
                                setDeleteMenuAnchor(e.currentTarget);
                                setDeleteMenuGroup(group);
                              }}
                            >
                              Delete
                            </button>
                          )}
                          {deleteMenuAnchor && deleteMenuGroup === group && (
                            <Popover
                              anchorEl={deleteMenuAnchor}
                              onClose={() => {
                                setDeleteMenuAnchor(null);
                                setDeleteMenuGroup(null);
                              }}
                            >
                              {group.digital && (
                                <button
                                  type="button"
                                  className="bl-popover-item"
                                  onClick={(e) => {
                                    handleDelete(group.digital.book_id, e);
                                    setDeleteMenuAnchor(null);
                                    setDeleteMenuGroup(null);
                                  }}
                                >
                                  Delete Digital
                                </button>
                              )}
                              {group.print && (
                                <button
                                  type="button"
                                  className="bl-popover-item"
                                  onClick={(e) => {
                                    handleDelete(group.print.book_id, e);
                                    setDeleteMenuAnchor(null);
                                    setDeleteMenuGroup(null);
                                  }}
                                >
                                  Delete Print
                                </button>
                              )}
                            </Popover>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>

        {filteredBooks.length > 0 && (
          <div className="pagination">
            <span className="page-info">
              Showing {showingFrom} to {showingTo} of {filteredBooks.length} entries
              {selectedKeys.length ? ` · ${selectedKeys.length} selected` : ''}
            </span>
            {totalPages > 1 && (
              <>
                <button
                  type="button"
                  className="page-btn"
                  onClick={() => setCurrentPage((prev) => Math.max(1, prev - 1))}
                  disabled={currentPage === 1}
                  aria-label="Previous page"
                >
                  ‹
                </button>
                {pageItems(currentPage, totalPages).map((item, idx) =>
                  item === '…' ? (
                    <button key={`e-${idx}`} type="button" className="page-btn" disabled>
                      …
                    </button>
                  ) : (
                    <button
                      key={item}
                      type="button"
                      className={`page-btn${item === currentPage ? ' active' : ''}`}
                      onClick={() => setCurrentPage(item)}
                    >
                      {item}
                    </button>
                  )
                )}
                <button
                  type="button"
                  className="page-btn"
                  onClick={() => setCurrentPage((prev) => Math.min(totalPages, prev + 1))}
                  disabled={currentPage === totalPages}
                  aria-label="Next page"
                >
                  ›
                </button>
              </>
            )}
          </div>
        )}
      </div>

      {assignModalOpen && (
        <div className="bl-modal-overlay" onClick={() => !assigning && setAssignModalOpen(false)}>
          <div className="bl-modal" onClick={(e) => e.stopPropagation()} role="dialog" aria-modal="true">
            <div className="bl-modal-head">
              <h3>Assign books to users</h3>
              <button
                type="button"
                className="bl-modal-close"
                onClick={() => setAssignModalOpen(false)}
                disabled={assigning}
                aria-label="Close"
              >
                ×
              </button>
            </div>
            <p className="bl-modal-desc">
              Assign <strong>{selectedKeys.length}</strong> selected book
              {selectedKeys.length === 1 ? '' : 's'} to one or more users.
            </p>
            <input
              type="search"
              className="text-input"
              style={{ width: '100%', marginBottom: 12 }}
              placeholder="Search users by name or email…"
              value={assignUserSearch}
              onChange={(e) => setAssignUserSearch(e.target.value)}
            />
            <div className="bl-user-checklist">
              {filteredUsers.length === 0 ? (
                <div className="empty-note">No users found.</div>
              ) : (
                filteredUsers.map((user) => {
                  const checked = assignUserIds.includes(user.user_id);
                  return (
                    <label key={user.user_id} className={`bl-user-check${checked ? ' is-checked' : ''}`}>
                      <input
                        type="checkbox"
                        checked={checked}
                        onChange={() =>
                          setAssignUserIds((prev) =>
                            prev.includes(user.user_id)
                              ? prev.filter((id) => id !== user.user_id)
                              : [...prev, user.user_id]
                          )
                        }
                      />
                      <span>
                        <strong>{user.name || 'Unnamed'}</strong>
                        <small>{user.email}</small>
                      </span>
                    </label>
                  );
                })
              )}
            </div>
            <div className="bl-modal-actions">
              <button
                type="button"
                className="btn btn-secondary"
                onClick={() => setAssignModalOpen(false)}
                disabled={assigning}
              >
                Cancel
              </button>
              <button
                type="button"
                className="btn btn-primary"
                onClick={handleBulkAssign}
                disabled={assigning || !assignUserIds.length}
              >
                {assigning
                  ? 'Assigning…'
                  : `Assign to ${assignUserIds.length || 0} user${assignUserIds.length === 1 ? '' : 's'}`}
              </button>
            </div>
          </div>
        </div>
      )}

      {assigneesModal && (
        <div className="bl-modal-overlay" onClick={() => setAssigneesModal(null)}>
          <div className="bl-modal" onClick={(e) => e.stopPropagation()} role="dialog" aria-modal="true">
            <div className="bl-modal-head">
              <h3>Assigned users</h3>
              <button
                type="button"
                className="bl-modal-close"
                onClick={() => setAssigneesModal(null)}
                aria-label="Close"
              >
                ×
              </button>
            </div>
            <p className="bl-modal-desc">
              Users with access to <strong>{assigneesModal.title || 'this book'}</strong>
            </p>
            <div className="bl-assignee-list">
              {(assigneesModal.assigned_users || []).length === 0 ? (
                <div className="empty-note">No users assigned to this book yet.</div>
              ) : (
                (assigneesModal.assigned_users || []).map((user) => (
                  <div key={user.user_id} className="bl-assignee-row">
                    <div>
                      <strong>{user.name || 'Unnamed'}</strong>
                      <small>{user.email}</small>
                    </div>
                    <button
                      type="button"
                      className="btn-quiet ma-unassign-btn bl-unassign-user"
                      disabled={unassigningUserId === user.user_id}
                      onClick={() => handleUnassignUserFromGroup(assigneesModal, user)}
                    >
                      {unassigningUserId === user.user_id ? 'Removing…' : 'Unassign'}
                    </button>
                  </div>
                ))
              )}
            </div>
            <div className="bl-modal-actions">
              <button type="button" className="btn btn-secondary" onClick={() => setAssigneesModal(null)}>
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default BooksTable;
